import { Check, ClipboardCopy, Download, UserRoundPlus, X } from 'lucide-react';
import QRCode from 'qrcode';

import { useEffect, useRef, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Button, Card } from '@splitfiesta/ui';

import { ShareIcon } from '@/components/common/ShareIcon';
import { MemberActionRow } from '@/components/members/MemberActionRow';
import { Portal } from '@/components/ui/Portal';

import { QR_COLORS } from '@/lib/qrColors';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/** The printable PNG: a high-resolution QR with the brand line under it. */
async function renderBrandedPng(
  link: string,
  brand: string,
  caption: string
): Promise<string> {
  const qrSize = 500;
  const brandingHeight = 120;
  const padding = 40;

  const qrDataUrl = await QRCode.toDataURL(link, {
    width: 800,
    margin: 2,
    color: QR_COLORS,
    errorCorrectionLevel: 'H',
  });
  const qrImage = await loadImage(qrDataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = qrSize + padding * 2;
  canvas.height = qrSize + brandingHeight + padding * 2;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(qrImage, padding, padding, qrSize, qrSize);

  const textX = canvas.width / 2;
  const brandingY = qrSize + padding + 30;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#000000';
  ctx.font = 'bold 36px Arial, sans-serif';
  ctx.fillText(brand, textX, brandingY);
  ctx.font = '20px Arial, sans-serif';
  ctx.fillText(caption, textX, brandingY + 45);

  return canvas.toDataURL('image/png', 1.0);
}

export interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  groupName: string;
  /** Full invite link, `?lang=` included. */
  link: string;
  copied: boolean;
  onCopy: () => void;
  onShare: () => void;
  /** Renders the "add a member without an account" row when provided. */
  onAddPlaceholder?: () => void;
}

export function InviteDialog({
  open,
  onClose,
  groupName,
  link,
  copied,
  onCopy,
  onShare,
  onAddPlaceholder,
}: InviteDialogProps) {
  const { t } = useTranslation();
  useNamespace('common');
  useNamespace('groups');

  // A callback ref, not useRef: the Portal mounts its children a tick after
  // the dialog opens, and the QR effect must rerun once the canvas exists.
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [qrResult, setQrResult] = useState<{
    key: string;
    png: string;
    failed: boolean;
  } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const brand = t('common:invitations.brandNameWithDomain');
  const caption = t('common:invitations.scanToJoin', { groupName });
  // Keyed so a stale result for another link never shows while redrawing.
  const qrKey = `${link}|${brand}|${caption}`;
  const current = qrResult?.key === qrKey ? qrResult : null;
  const brandedPng = current?.png ?? '';
  const qrFailed = current?.failed ?? false;

  useEffect(() => {
    if (!canvas || !link) return;

    let cancelled = false;

    QRCode.toCanvas(canvas, link, { width: 256, margin: 2, color: QR_COLORS })
      .then(() => renderBrandedPng(link, brand, caption))
      .then(png => {
        if (!cancelled) setQrResult({ key: qrKey, png, failed: false });
      })
      .catch(() => {
        if (!cancelled) setQrResult({ key: qrKey, png: '', failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [canvas, link, brand, caption, qrKey]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open && canvas) panelRef.current?.focus();
  }, [open, canvas]);

  const handleDownload = () => {
    if (!brandedPng) return;
    const anchor = document.createElement('a');
    anchor.download = `${groupName}-invitation-qr.png`;
    anchor.href = brandedPng;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  if (!open) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50"
          onClick={onClose}
          data-testid="invite-dialog-backdrop"
        />
        <Card
          className="relative w-full max-w-md max-h-full overflow-y-auto"
          padding="none"
          noHoverEffect
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="invite-dialog-title"
            tabIndex={-1}
            className="p-6 focus:outline-hidden"
            data-testid="invite-dialog"
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2
                  id="invite-dialog-title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t('common:invitations.qrCodeTitle', { groupName })}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('common:invitations.shareLinkInstruction')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="p-2 -mt-2 -mr-2 shrink-0"
                aria-label={t('common:actions.close')}
                data-testid="invite-dialog-close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex justify-center mb-5">
              <div className="p-4 bg-white rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                <canvas
                  ref={setCanvas}
                  width={256}
                  height={256}
                  className={qrFailed ? 'hidden' : 'block mx-auto mb-3'}
                  data-testid="qr-code-canvas"
                />
                {qrFailed ? (
                  <p
                    className="max-w-[256px] text-sm text-gray-600"
                    data-testid="qr-code-error"
                  >
                    {t('common:invitations.qrGenerationFailed')}
                  </p>
                ) : (
                  <>
                    <div className="text-lg font-bold text-gray-800 mb-1">
                      {brand}
                    </div>
                    <div className="text-sm text-gray-600">{caption}</div>
                  </>
                )}
              </div>
            </div>

            <div
              className="mb-5 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm text-gray-600 dark:text-gray-400 break-all select-all"
              data-testid="invite-link"
            >
              {link}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={onCopy}
                disabled={copied}
                data-testid="copy-invite-button"
              >
                {copied ? (
                  <Check className="w-4 h-4 mr-1.5" />
                ) : (
                  <ClipboardCopy className="w-4 h-4 mr-1.5" />
                )}
                {copied ? t('common:copied') : t('common:copy')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={onShare}
                data-testid="share-invite-button"
              >
                <ShareIcon className="w-4 h-4 mr-1.5" />
                {t('common:share')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownload}
                disabled={!brandedPng}
                data-testid="download-qr-button"
              >
                <Download className="w-4 h-4 mr-1.5" />
                {t('common:download')}
              </Button>
            </div>

            {onAddPlaceholder && (
              <div className="mt-5 -mx-6 -mb-6 border-t border-gray-100 dark:border-gray-700">
                <MemberActionRow
                  icon={UserRoundPlus}
                  label={t('groups:placeholderMembers.addCta')}
                  onClick={onAddPlaceholder}
                  testId="invite-add-placeholder"
                />
              </div>
            )}
          </div>
        </Card>
      </div>
    </Portal>
  );
}

export default InviteDialog;
